/**
 * loopLayout — THE ROUTINE AS A GRAPH (NPC life, 10-23 — the Chair: "the loop as the routine, and the behavior graph as where they are in
 * that routine" · "The trace log shows the routine as a graph with the branching option around the node of discovery … that's the transit
 * time between routine tasks"). Its ring's steps as NODES in rows, each wired out of its right pin into the next one's left pin, the last
 * wired round the outside back to the first — one closed circuit. A wire is the TRANSIT between two steps: where the dot runs while it
 * walks, and where a DISCOVERY sits (a knot) with its options branching round it. Laid out in a fixed design space (W × H); a view
 * scales it (the trace's 100 % fills the panel, 75 % / 25 % shows it small at the left).
 */
export const W = 960, H = 400

export interface LoopNode { n: string; label: string; x: number; y: number; w: number; h: number; in: [number, number]; out: [number, number] }
export interface LoopWire { from: number; to: number; pts: [number, number][]; d: string; len: number }

/** a step's name as people read it (the ring's second Work is "Work 2" inside — "WORK" here) */
export const labelOf = (n: string) => n.replace(/ \d+$/, '').toUpperCase()

/** a path through `pts` with its corners rounded (radius r) */
function rounded(pts: [number, number][], r = 10): string {
  if (pts.length < 2) return ''
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`
  for (let i = 1; i < pts.length - 1; i++) {
    const [px, py] = pts[i - 1], [cx, cy] = pts[i], [nx, ny] = pts[i + 1]
    const a = Math.hypot(cx - px, cy - py), b = Math.hypot(nx - cx, ny - cy), k = Math.min(r, a / 2, b / 2)
    const sx = cx - ((cx - px) / (a || 1)) * k, sy = cy - ((cy - py) / (a || 1)) * k, ex = cx + ((nx - cx) / (b || 1)) * k, ey = cy + ((ny - cy) / (b || 1)) * k
    d += ` L${sx.toFixed(1)},${sy.toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${ex.toFixed(1)},${ey.toFixed(1)}`
  }
  const [lx, ly] = pts[pts.length - 1]
  return d + ` L${lx.toFixed(1)},${ly.toFixed(1)}`
}
const lengthOf = (pts: [number, number][]) => pts.slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]), 0)

/** the point `f` (0…1) of the way along a wire */
export function along(w: LoopWire, f: number): [number, number] {
  let left = Math.max(0, Math.min(1, f)) * w.len
  for (let i = 1; i < w.pts.length; i++) {
    const [ax, ay] = w.pts[i - 1], [bx, by] = w.pts[i], seg = Math.hypot(bx - ax, by - ay)
    if (left <= seg) { const t = seg ? left / seg : 0; return [ax + (bx - ax) * t, ay + (by - ay) * t] }
    left -= seg
  }
  return w.pts[w.pts.length - 1]
}

/** THE GRAPH OF A RING: its steps' names in order → nodes in rows (a few columns, as square as the panel), wires between them */
export function loopLayout(names: string[]): { nodes: LoopNode[]; wires: LoopWire[] } {
  const N = names.length
  if (!N) return { nodes: [], wires: [] }
  // (rows of at most six — wires long enough to hold a discovery between two steps; the rows as even as they come)
  const R = Math.max(1, Math.ceil(N / 6)), C = Math.max(2, Math.ceil(N / R))
  const L = 64, Rm = 34, T = 50, B = 50
  const nw = Math.min(124, (W - L - Rm) / C - 44), nh = 24
  const px = C > 1 ? (W - L - Rm - nw) / (C - 1) : 0, py = R > 1 ? (H - T - B - nh - 10) / (R - 1) : 0
  const nodes: LoopNode[] = names.map((n, i) => {
    const r = Math.floor(i / C), c = i % C, x = L + c * px, y = T + r * py + (c % 2 ? 10 : 0)
    return { n, label: labelOf(n), x, y, w: nw, h: nh, in: [x, y + nh / 2], out: [x + nw, y + nh / 2] }
  })
  const wires: LoopWire[] = nodes.map((a, i) => {
    const j = (i + 1) % N, b = nodes[j], ra = Math.floor(i / C), rb = Math.floor(j / C)
    let pts: [number, number][]
    if (j === 0) {
      // (the last round the outside — down below the rows, along to the left, up — into the first: the circuit closed)
      const by = H - 20, lx = 22
      pts = [a.out, [a.out[0] + 16, a.out[1]], [a.out[0] + 16, by], [lx, by], [lx, b.in[1]], b.in]
    } else if (ra === rb) {
      const mx = (a.out[0] + b.in[0]) / 2
      pts = a.out[1] === b.in[1] ? [a.out, b.in] : [a.out, [mx, a.out[1]], [mx, b.in[1]], b.in]
    } else {
      // (the end of a row into the start of the next: out, down into the gap between the rows, back along it, down, in)
      const gy = T + ra * py + nh + 10 + (py - nh - 10) / 2
      pts = [a.out, [a.out[0] + 16, a.out[1]], [a.out[0] + 16, gy], [b.in[0] - 16, gy], [b.in[0] - 16, b.in[1]], b.in]
    }
    return { from: i, to: j, pts, d: rounded(pts), len: lengthOf(pts) }
  })
  return { nodes, wires }
}
